import React, { lazy, Suspense, useEffect, useState } from "react";
import { Hero } from "./components/Hero/Hero";
import { Navbar } from "./components/Navbar/Navbar";
import CustomCursor from "./components/Cursor/CustomCursor";
import ButterflyEffect from "./components/Cursor/ButterflyEffect";
import Preloader from "./components/Preloader/Preloader";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import ChatBot from "./components/ChatBot/ChatBot";
import "./vars.css";
import "./App.css";
import AOS from "aos";
import "aos/dist/aos.css";

// ── Lazy-load everything below the fold ─────────────────────────────────────
// These chunks are only downloaded when the browser needs them,
// dramatically reducing initial bundle parse + execution time.
const Service    = lazy(() => import("./components/Services/Service"));
const Experience = lazy(() =>
  import("./components/Experience/Experience").then(m => ({ default: m.Experience }))
);
const Skills  = lazy(() => import("./components/Skills/Skills"));
const Projects = lazy(() =>
  import("./components/Projects/Projects").then(m => ({ default: m.Projects }))
);
const Blogs   = lazy(() => import("./components/Blogs/Blogs"));
const Contact = lazy(() =>
  import("./components/Contact/Contact").then(m => ({ default: m.Contact }))
);
const Footer  = lazy(() => import("./components/Footer/Footer"));

// Invisible placeholder — zero layout shift while chunks load
const SectionFallback = () => <div style={{ minHeight: "80px" }} aria-hidden="true" />;

function App() {
  const [loader, setLoader] = useState(true);
  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || "light"
  );

  useEffect(() => {
    AOS.init({
      duration: 800,        // snappier than default 1000ms
      once: true,           // ← KEY: never re-animate on scroll back (big perf win)
      offset: 80,
      easing: "ease-out-cubic",
    });
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    const id = setTimeout(() => setLoader(false), 1600);
    return () => clearTimeout(id);
  }, []);

  const toggleTheme = () => setTheme(t => (t === "light" ? "dark" : "light"));

  return (
    <ErrorBoundary>
      <CustomCursor />
      <ButterflyEffect />
      {loader && <Preloader />}
      <Navbar setLoader={setLoader} theme={theme} toggleTheme={toggleTheme} />

      {/* Hero is above-fold — loaded eagerly, no lazy */}
      <Hero setLoader={setLoader} />

      {/* All below-fold sections are code-split and lazy-loaded */}
      <Suspense fallback={<SectionFallback />}>
        <Service />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Experience />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Skills />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Projects />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Blogs />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Contact />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Footer />
      </Suspense>
      <ChatBot />
    </ErrorBoundary>
  );
}

export default App;
